'use client';

import { useState } from 'react';
import Image from 'next/image';
import clsx from 'clsx';

import { CollapseCard } from '@shared/ui/components';
import Splash from '@public/images/splash.svg';
import Glass from '@public/images/glass-2.svg';
import ArrowIcon from '@public/icons/arrow-right.svg';

import styles from './CourseModules.module.scss';

const modules = [
  {
    id: 1,
    title: 'Module 1',
    fullTitle: 'Introduction to Quantum Computing',
    description: '4 weeks / Part of Quantum Basic, Quantum Professional and Quantum Academia',
    features: [
      'What is Quantum Computing',
      'Linear Algebra Fundamentals',
      'Quantum Spin',
      'Qubits and Superposition',
      'Measurement in Quantum Systems',
      'Quantum Teleportation',
      'Entanglement',
    ],
  },
  {
    id: 2,
    title: 'Module 2',
    fullTitle: 'Ethical Principles in Quantum Computing',
    description: '3 weeks / Part of Quantum Basic, Quantum Professional and Quantum Academia',
    features: [
      'Data Privacy and Quantum Computing',
      'Quantum Security and Cryptography',
      'Societal Impacts of Quantum Technologies',
      'Ethical Dilemmas in Quantum Research and Applications',
      'Responsible Innovation in Quantum Computing',
      'Regulatory and Policy Considerations',
    ],
  },
  {
    id: 3,
    title: 'Module 3',
    fullTitle: 'Quantum Algorithms and Applications',
    description: '2 weeks / Part of Quantum Professional and Quantum Academia',
    features: [
      'Quantum Gates and Circuits',
      'Quantum Algorithms',
      'Applications of Quantum Algorithms',
      'Implementing Quantum Algorithms with Dynex SDK',
    ],
  },
  {
    id: 4,
    title: 'Module 4',
    fullTitle: 'Solving Optimization Problems with Dynex & Current Research',
    description: '2 weeks / Part of Quantum Academia',
    features: [
      'Dynex Platform Overview',
      'Quantum Annealing',
      'Solving Optimization Problems with Dynex',
      'Quantum Machine Learning',
      'Neuromorphic Computing and Quantum Computing',
      'Quantum Large Language Models (LLMs)',
      'Topological Quantum Computing',
      'Hybrid Quantum-Classical Algorithms',
      'Quantum Cryptography and Post-Quantum Security',
      'Dynex LLM and Dynex QPU Solution Chip',
    ],
  },
  {
    id: 5,
    title: 'Module 5',
    fullTitle: 'Capstone Project',
    description: '1 Week / Final Project - Integral Part of the "Quantum Academia" Program Plan',
    features: [],
  },
];

export default function CourseModules() {
  const [activeModule, setActiveModule] = useState<number | null>(1);

  return (
    <div id="what-you-learn" className={styles.container}>
      <div className={styles.title}>What You Learn</div>

      <div className={styles.modules}>
        {modules.slice(0, 4).map((module) => (
          <CollapseCard
            key={module.id}
            isOpen={activeModule === module.id}
            className={clsx(styles.module, activeModule === module.id && styles.module_active)}
            bodyClassName={styles.module_body}
            header={
              <>
                <Image
                  src={Splash}
                  className={clsx(
                    styles.module_splash,
                    activeModule === module.id && styles.module_splash_active,
                  )}
                  alt="Splash"
                  draggable={false}
                />

                <div className={styles.module_header}>
                  <div className={styles.module_header_info}>
                    <div className={styles.module_header_info_title}>{module.title}</div>
                    <div className={styles.module_header_info_fullTitle}>{module.fullTitle}</div>
                    <div className={styles.module_header_info_description}>
                      {module.description}
                    </div>
                  </div>

                  <div
                    className={clsx(
                      styles.module_header_toggle,
                      activeModule === module.id && styles.module_header_toggle_active,
                    )}
                  >
                    <Image src={ArrowIcon} alt="Arrow" draggable={false} />
                  </div>
                </div>
              </>
            }
            onClick={() => setActiveModule(activeModule === module.id ? null : module.id)}
          >
            <div className={styles.features}>
              {module.features.map((feat) => (
                <div key={feat} className={styles.feature}>
                  <div className={styles.feature_marker}>
                    <div />
                    <div />
                    <div />
                  </div>

                  <div>{feat}</div>
                </div>
              ))}
            </div>
          </CollapseCard>
        ))}

        <div className={styles.capstone}>
          <Image src={Glass} className={styles.capstone_glass} alt="Glass" draggable={false} />

          <div className={styles.capstone_title}>{modules[4].title}</div>
          <div className={styles.capstone_fullTitle}>{modules[4].fullTitle}</div>
          <div className={styles.capstone_description}>{modules[4].description}</div>
        </div>
      </div>
    </div>
  );
}
